/**
 * Pagos — pantalla unificada de cobros y pagos de préstamos.
 *
 * La vista principal es el informe de pagos realizados embebido (con
 * cancelación por fila para roles con ares4010+borrar). Tarjetas de acción:
 *   - "Cobrar pago" (ares4010): el formulario de cobro completo en diálogo a
 *     pantalla completa; con ares4015 el mismo formulario permite editar la
 *     fecha del pago (antes era la pantalla aparte "Cobro de Pagos Especial").
 *   - "Recibos por préstamo" (ares4030): mantenimiento — cancela el último
 *     recibo, reimprime y saca el reporte crediticio.
 * Los pagos cancelados se consultan con el filtro "Estado" del propio
 * listado (programa informepagoscancelados).
 *
 * Sustituye las entradas de menú de cobropagos.js, cobropagosespecial.js,
 * mantenimientopagos.js, informepagos.js e informepagoscancelados.js.
 */
import React, { useState } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import { useMaterialUIController } from "context";
import { FP, PageHeader, ActionCard } from "components/financepro";
import { tienePermisosModulo } from "lib/seguridad/permisosPorModulo";
import CobroPagos from "modules/pagos/cobropagos";
import MantenimientoPagos from "modules/pagos/mantenimientopagos";
import Informepagos from "modules/pagos/informepagos";
import useAccionInicial from "lib/useAccionInicial";

export default function Pagos() {
  const [controller] = useMaterialUIController();
  const { roleDetalles } = controller;

  // Permisos por programa ARES (el backend re-valida en permisos.py).
  const puedeCobrar = tienePermisosModulo(roleDetalles, "ares4010");
  const puedeMantenimiento = tienePermisosModulo(roleDetalles, "ares4030");
  // El listado principal acepta cualquiera de los programas que el backend
  // admite como alternativa para /api/informespagos, más el de cancelados
  // (el filtro "Estado" del listado cubre ese informe).
  const puedeListado =
    tienePermisosModulo(roleDetalles, "informepagos") ||
    tienePermisosModulo(roleDetalles, "ares7050") ||
    tienePermisosModulo(roleDetalles, "informepagoscancelados") ||
    puedeCobrar;

  const [cobroOpen, setCobroOpen] = useState(false);
  const [mantOpen, setMantOpen] = useState(false);

  // Enlace profundo desde el buscador global (?accion=...).
  useAccionInicial({
    cobrar: { permitido: puedeCobrar, abrir: () => setCobroOpen(true) },
    recibos: { permitido: puedeMantenimiento, abrir: () => setMantOpen(true) },
  });

  // Incrementarlo recarga el listado embebido (tras cobrar o cancelar pagos).
  const [refreshKey, setRefreshKey] = useState(0);
  const refrescar = () => setRefreshKey((k) => k + 1);

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Pagos"
        subtitle="Cobra cuotas, consulta los pagos realizados y gestiona recibos en un solo lugar"
      />

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {/* Acciones principales */}
        {(puedeCobrar || puedeMantenimiento) && (
          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            {puedeCobrar && (
              <Grid item xs={12} sm={6}>
                <ActionCard
                  icon={PaymentsOutlinedIcon}
                  titulo="Cobrar pago"
                  descripcion="Cuotas de un préstamo con mora, interés y capital"
                  color={FP.color.success}
                  onClick={() => setCobroOpen(true)}
                />
              </Grid>
            )}
            {puedeMantenimiento && (
              <Grid item xs={12} sm={6}>
                <ActionCard
                  icon={ReceiptLongOutlinedIcon}
                  titulo="Recibos por préstamo"
                  descripcion="Cancela el último recibo, reimprime y reporte crediticio"
                  color={FP.color.accentBlue}
                  onClick={() => setMantOpen(true)}
                />
              </Grid>
            )}
          </Grid>
        )}

        {/* Listado principal: el informe de pagos realizados, embebido */}
        {puedeListado && <Informepagos embedded refreshToken={refreshKey} />}
      </Box>

      {/* Cobro de pagos (formulario completo a pantalla completa) */}
      <Dialog fullScreen open={cobroOpen} onClose={() => setCobroOpen(false)}>
        <CobroPagos embedded onExit={() => { setCobroOpen(false); refrescar(); }} onSaved={refrescar} />
      </Dialog>

      {/* Mantenimiento de recibos por préstamo */}
      <Dialog fullScreen open={mantOpen} onClose={() => setMantOpen(false)}>
        <MantenimientoPagos embedded onExit={() => { setMantOpen(false); refrescar(); }} />
      </Dialog>

    </Box>
  );
}
